import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useTheme } from "@mui/material";
import { FunctionComponent } from "react";
import { InfoRounded, WarningRounded } from "@mui/icons-material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";

interface CustomerBaseModalProps {
    open: boolean,
    onClose?: () => void,
    contentDescription: string,
    buttonCloseDescription?: string,
    buttonConfirmDescription?: string,
    onConfirm?: () => void,
    /**
     * CR-DRIVALIA-EPCH (LPP-22837): terza azione OPZIONALE, per i casi in cui la scelta non e'
     * binaria (es. "sostituisci entrambe le e-mail" / "solo questa" / "annulla").
     * Se la label non viene passata il pulsante non viene reso: le modali esistenti, che usano
     * solo chiudi/conferma, restano identiche.
     */
    buttonAlternativeDescription?: string,
    onAlternative?: () => void,
    /**
     * CR-DRIVALIA-EPCH (LPP-22837): conferma in lavorazione. Tutti i pulsanti si disabilitano e quello
     * di conferma mostra uno spinner (con confirmLoadingDescription, se passata), cosi' e' chiaro che
     * l'azione e' stata recepita e non la si ripete. Opzionale: le modali esistenti restano identiche.
     */
    confirmLoading?: boolean,
    confirmLoadingDescription?: string,
}

const CustomerBaseModal: FunctionComponent<CustomerBaseModalProps> = ({
    open,
    onClose,
    contentDescription,
    buttonCloseDescription,
    buttonConfirmDescription,
    onConfirm,
    buttonAlternativeDescription,
    onAlternative,
    confirmLoading = false,
    confirmLoadingDescription
}) => {

    const theme = useTheme();
    const { t } = useTranslation(["customerbase"]);

    return (
        <Dialog open={open} onClose={() => { return; }} >
            <DialogTitle>
                <Box sx={{display: "flex", alignItems: "center"}}>
                    <WarningRounded color={"warning"}/>
                    <Typography color={"primary"} fontWeight={"bold"} sx={{paddingLeft: 1}}>
                        {t("editingModal.warningTitle")}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent >
                <Box sx={{padding: "15px", display: "flex", alignItems: "center", justifyContent: "center"}} >
                    <Typography color="primary" variant="body1">
                        {contentDescription}
                    </Typography>
                </Box> 
            </DialogContent>
            <DialogActions sx={{backgroundColor: theme.palette.neutral.bright, padding: "15px"}}>
                <Stack direction="row" columnGap={1}>
                    {buttonCloseDescription && 
                        <Button 
                            variant="outlined"
                            disableElevation
                            color="accent"
                            sx={{backgroundColor: theme.palette.neutral.bright}}
                            disabled={confirmLoading}
                            onClick={onClose}
                        >
                            <Typography fontWeight={"bold"} variant="subtitle2" color="accent" lineHeight={0}>
                                {buttonCloseDescription}
                            </Typography>
                        </Button>
                    }
                    {/* CR-DRIVALIA-EPCH (LPP-22837): terza azione opzionale, resa solo se valorizzata */}
                    {buttonAlternativeDescription &&
                        <Button
                            variant="outlined"
                            disableElevation
                            color="accent"
                            sx={{backgroundColor: theme.palette.neutral.bright}}
                            disabled={confirmLoading}
                            onClick={onAlternative}
                        >
                            <Typography fontWeight={"bold"} variant="subtitle2" color="accent" lineHeight={0}>
                                {buttonAlternativeDescription}
                            </Typography>
                        </Button>
                    }
                    {buttonConfirmDescription && 
                        <Button 
                            color="accent"
                            variant="contained"
                            disableElevation
                            disabled={confirmLoading}
                            startIcon={confirmLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                            onClick={onConfirm}
                        >
                            <Typography fontWeight={"bold"} variant="subtitle2" color="accent" lineHeight={0}>
                                {confirmLoading && confirmLoadingDescription ? confirmLoadingDescription : buttonConfirmDescription}
                            </Typography>
                        </Button>
                    }                    
                </Stack>
            </DialogActions>
        </Dialog>
    );

}

export default CustomerBaseModal;